import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Steps from "antd/es/steps";
import { useIntl } from "react-intl";
import { useHistory, useRouteMatch } from "react-router-dom";

import { IntegrationMetadataInstance } from "@mapmycustomers/shared/types/integrations/IntegrationMetadataInstance";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { isOnTrialPlan } from "@app/store/iam";
import { IntegrationFieldsMapping } from "@app/store/integrations";
import {
  changeOrgUrl,
  deleteIntegration,
  initializeEditPage,
} from "@app/store/integrations/actions";
import {
  areIntegrationFieldsChanged,
  areIntegrationUsersChanged,
  doNeedToSelectDynamicsInstance,
  getCurrentIntegration,
  getIntegrationFields,
  getIntegrationUsers,
  getLoading,
  isIntegrationChanged,
} from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import IntegrationUser from "@app/types/integrations/IntegrationUser";
import getStepNames from "@app/util/integrations/getStepNames";
import useServiceDescription from "@app/util/integrations/useServiceDescription";

import SettingPage from "../common/SettingPage";

import LimitAlert from "./components/LimitAlert";
import SelectDynamicsInstanceModal from "./components/SelectDynamicsInstanceModal";
import ButtonBar from "./components/steps/ButtonBar";
import Step1 from "./components/steps/Step1";
import Step2 from "./components/steps/Step2";
import Step3 from "./components/steps/Step3";
import Step4 from "./components/steps/Step4";
import IntegrationEditStep from "./enum/IntegrationEditStep";
import { getIntegrationAnalyticsSetupIssuer } from "./integrationAnalytics";
import styles from "./Integrations.module.scss";
import useNextButtonState from "./utils/useNextButtonState";
import useSyncedEntityTypes from "./utils/useSyncedEntityTypes";

interface Props {
  areIntegrationFieldsOrUsersChanged: boolean;
  changeOrgUrl: typeof changeOrgUrl.request;
  currentIntegration?: Integration;
  deleteIntegration: typeof deleteIntegration.request;
  initializeEditPage: typeof initializeEditPage.request;
  integrationChanged: boolean;
  integrationFields: IntegrationFieldsMapping;
  integrationUsers: IntegrationUser[];
  isTrial?: boolean;
  loading: boolean;
  needToSelectDynamicsInstance: boolean;
}

type RouteParams = {
  integrationId: string;
};

const EditIntegration: React.FC<Props> = ({
  areIntegrationFieldsOrUsersChanged,
  changeOrgUrl,
  currentIntegration,
  deleteIntegration,
  initializeEditPage,
  integrationChanged,
  integrationFields,
  integrationUsers,
  isTrial,
  loading,
  needToSelectDynamicsInstance,
}) => {
  const { params } = useRouteMatch<RouteParams>();
  const intl = useIntl();

  const steps = getStepNames(intl);
  const history = useHistory();
  const serviceDescription = useServiceDescription(currentIntegration?.type);

  const [currentStep, setStep] = useState(IntegrationEditStep.CHOOSE);

  const analyticIssuer = useMemo(
    () => getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, currentStep + 1),
    [currentIntegration?.type, currentStep]
  );

  useEffect(() => {
    initializeEditPage(parseInt(params.integrationId));
  }, [initializeEditPage, params.integrationId]);

  const handleHideDynamicsInstanceSelection = useCallback(
    (value?: IntegrationMetadataInstance["instanceUrl"]) => {
      if (value) {
        changeOrgUrl(value);
      } else {
        deleteIntegration(currentIntegration!.id);
        history.push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_INTEGRATIONS}`);
      }
    },
    [changeOrgUrl, currentIntegration, deleteIntegration, history]
  );

  const handleNextStep = useCallback(() => {
    analyticIssuer.clicked(["Continue To Next Step"]);
    setStep((step: number) => (step < IntegrationEditStep.FINISH ? step + 1 : step));
  }, [analyticIssuer, setStep]);

  const handlePreviousStep = useCallback(() => {
    analyticIssuer.clicked(["Go Back"]);
    setStep((step: number) => (step > IntegrationEditStep.CHOOSE ? step - 1 : step));
  }, [analyticIssuer, setStep]);

  useEffect(() => {
    document.querySelector(".mmc-scroll-pane")?.scrollTo({ behavior: "smooth", top: 0 });
  }, [currentStep]);

  const syncedEntityTypes = useSyncedEntityTypes(
    currentIntegration,
    serviceDescription?.supportedEntityTypes
  );

  const [nextStepDisabled, nextButtonErrorMessage] = useNextButtonState(
    intl,
    currentStep,
    integrationUsers,
    integrationFields,
    syncedEntityTypes,
    currentIntegration
  );

  const trialAlert = isTrial && <LimitAlert className={styles.limitAlert} />;

  return (
    <SettingPage
      actions={
        <Button
          href="https://support.mapmycustomers.com/hc/en-us/articles/360053375574-HubSpot-Integration-Overview"
          leftIcon={<FontAwesomeIcon icon={faQuestionCircle} />}
          target="_blank"
          type="link"
        >
          {intl.formatMessage(
            {
              id: "integrations.edit.goToIntegrations",
              defaultMessage: "Step-by-step instructions",
              description: "ext of Go to instructions button",
            },
            { name: serviceDescription?.name }
          )}
        </Button>
      }
      footer={
        currentStep !== IntegrationEditStep.FINISH && (
          <ButtonBar
            analyticIssuer={analyticIssuer}
            areIntegrationFieldsOrUsersChanged={areIntegrationFieldsOrUsersChanged}
            currentStep={currentStep}
            isNewIntegration={!currentIntegration?.schedule}
            isTrial={isTrial}
            nextButtonErrorMessage={nextButtonErrorMessage}
            nextDisabled={nextStepDisabled}
            onNextClick={handleNextStep}
            onPreviousClick={handlePreviousStep}
          />
        )
      }
      subTitle={intl.formatMessage(
        {
          id: "integrations.edit.header.subTitle",
          defaultMessage:
            "Follow the step-by-step instructions to sync your {name} data into Map My Customers",
          description: "SubTitle of a Integration edit header",
        },
        { name: serviceDescription?.name }
      )}
      title={intl.formatMessage(
        {
          id: "integrations.edit.header.title",
          defaultMessage: "Set up your {name} Integration",
          description: "Title of a Integration edit header",
        },
        { name: serviceDescription?.name }
      )}
    >
      {loading || !currentIntegration || !serviceDescription ? (
        <LoadingSpinner />
      ) : (
        <>
          <Steps className={styles.editSteps} current={currentStep}>
            {steps.map((title) => (
              <Steps.Step key={title} title={title} />
            ))}
          </Steps>
          <div className={styles.stepContent}>
            {currentStep === IntegrationEditStep.CHOOSE && (
              <>
                {trialAlert}
                <Step1
                  key={IntegrationEditStep.CHOOSE}
                  selectedService={serviceDescription}
                  supportedEntityTypes={serviceDescription.supportedEntityTypes}
                  syncOptions={currentIntegration.syncOptions}
                />
              </>
            )}
            {currentStep === IntegrationEditStep.MAP && (
              <>
                {trialAlert}
                <Step2
                  key={IntegrationEditStep.MAP}
                  serviceName={serviceDescription.name ?? ""}
                  syncedEntityTypes={syncedEntityTypes}
                />
              </>
            )}
            {currentStep === IntegrationEditStep.FREQUENCY && (
              <>
                {trialAlert}
                <Step3
                  key={IntegrationEditStep.FREQUENCY}
                  serviceName={serviceDescription.name ?? ""}
                />
              </>
            )}
            {currentStep === IntegrationEditStep.FINISH && (
              <Step4 selectedService={serviceDescription} />
            )}
          </div>
        </>
      )}
      {needToSelectDynamicsInstance && (
        <SelectDynamicsInstanceModal
          instances={currentIntegration?.metadata?.instances!}
          onHide={handleHideDynamicsInstanceSelection}
        />
      )}
      <InterceptNavigationModal
        shouldShowPrompt={currentStep !== IntegrationEditStep.FINISH && integrationChanged}
        subTitle={intl.formatMessage({
          id: "integrations.edit.confirmLeave.message",
          defaultMessage: "You will lose any progress made setting up or editing this integration.",
        })}
        title={intl.formatMessage({
          id: "integrations.edit.confirmLeave.title",
          defaultMessage: "Are you sure you want to leave this page?",
        })}
      />
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  areIntegrationFieldsOrUsersChanged:
    areIntegrationFieldsChanged(state) || areIntegrationUsersChanged(state),
  currentIntegration: getCurrentIntegration(state),
  integrationChanged: isIntegrationChanged(state),
  integrationFields: getIntegrationFields(state),
  integrationUsers: getIntegrationUsers(state),
  isTrial: isOnTrialPlan(state),
  loading: getLoading(state),
  needToSelectDynamicsInstance: doNeedToSelectDynamicsInstance(state),
});

const mapDispatchToProps = {
  changeOrgUrl: changeOrgUrl.request,
  deleteIntegration: deleteIntegration.request,
  initializeEditPage: initializeEditPage.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditIntegration);
